import type { ReactNode } from "react";
import { useTranslate } from "@refinedev/core";
import { HeartIcon, UserIcon, InvoiceIcon, HomeIcon } from "@fawkes/ui";

// Search-migration E3 — shared vocabulary for the header dropdown (GlobalSearch) and the basic
// `/zoekresultaten` route (SearchResults). Mirrors the E1 backend contract (`GET /api/search`):
//   { query, limitPerType, tookMs, total, groups{ animals[], relations[], invoices[], shelters[] }, facets }
// Each hit ships a structured (targetType, targetId) navigation target (Decision NAV) the shell maps to
// (Page, detailId) — never a Jmix route string. Classification fields (Decision SS) are url-value codes
// (consumed by the E4 facets/badges); E3 renders the type badge + the title/subtitle/initials only.

/** Display/group type of a hit, as sent on the wire (SearchHitResponse.type / targetType). */
export type SearchHitType = "animal" | "relation" | "invoice" | "stay";

export interface SearchHitDto {
  type: SearchHitType;
  id: string;
  title: string;
  subtitle: string | null;
  initials: string;
  /** Structured nav target the shell maps to (Page, detailId). A stay hit targets its animal. */
  targetType: SearchHitType | null;
  targetId: string | null;
  // Classification (Decision SS) — url-value codes; rendered by E4's facets/status badges, not E3.
  animalStatus: string | null;
  species: string | null;
  admissionDate: string | null;
  relationType: string | null;
  relationRoles: string[] | null;
  invoiceStatus: string | null;
}

/** Plural group key → its display type + group-header i18n key. */
type GroupKey = "animals" | "relations" | "invoices" | "shelters";

export interface SearchResponseDto {
  query: string;
  limitPerType: number;
  tookMs: number;
  total: number;
  groups: Partial<Record<GroupKey, SearchHitDto[]>>;
  facets?: unknown;
}

/** Minimum characters before the dropdown/route queries the backend (Jmix MIN_CHARS). */
export const MIN_CHARS = 3;
/** Per-type result cap for the header dropdown (Jmix LIMIT_PER_TYPE). */
export const DROPDOWN_LIMIT = 10;
/** Per-type result cap for the basic results route. */
export const RESULTS_LIMIT = 50;
/** Debounce before a keystroke triggers a search (Jmix DEBOUNCE_MS). */
export const DEBOUNCE_MS = 250;

/** The dropdown's type tabs (Alles/Dieren/Relaties/Facturen) — no Verblijven tab (Jmix parity). */
export type DropdownTab = "all" | "animal" | "relation" | "invoice";

/**
 * Render order of the groups — Dieren / Relaties / Facturen / Verblijven (Jmix
 * GlobalSearchHit.Type.values() order). Shelter hits group under "Alles" even though there is no
 * Verblijven tab.
 */
export const GROUP_ORDER: { key: GroupKey; display: SearchHitType; labelKey: string }[] = [
  { key: "animals", display: "animal", labelKey: "search.group.animals" },
  { key: "relations", display: "relation", labelKey: "search.group.relations" },
  { key: "invoices", display: "invoice", labelKey: "search.group.invoices" },
  { key: "shelters", display: "stay", labelKey: "search.group.shelters" },
];

/** Type → badge-label i18n key (Jmix GlobalSearchHit.getBadgeLabel: Dier/Relatie/Factuur/Verblijf). */
export const BADGE_KEY: Record<SearchHitType, string> = {
  animal: "search.badge.animal",
  relation: "search.badge.relation",
  invoice: "search.badge.invoice",
  stay: "search.badge.stay",
};

/** Group-header glyph for a display type (Jmix iconFor: HEART/USERS/FILE_TEXT/HOME). */
export function groupTypeIcon(type: SearchHitType): ReactNode {
  switch (type) {
    case "animal":
      // Een hartje en geen pootje: zo staat het op acc, bij de sectiekop én in het lege fotovlak.
      return <HeartIcon width={15} height={15} />;
    case "relation":
      return <UserIcon width={15} height={15} />;
    case "invoice":
      return <InvoiceIcon width={15} height={15} />;
    case "stay":
      return <HomeIcon width={15} height={15} />;
  }
}

// Jmix question-detection patterns (GlobalSearch.isQuestionLike) — kept verbatim. `boven ` has the
// trailing space on purpose.
const QUESTION_PATTERNS = [
  "langer dan",
  "zonder chip",
  "met plek",
  "verlopen ent",
  "openstaande factur",
  "vervallen factur",
  "boven ",
  "pleeggezin",
];

/**
 * Heuristic (verbatim port of GlobalSearch.isQuestionLike): does this look like a Dutch question rather
 * than a short search term? True when the (trimmed) input has ≥4 words OR contains a known question
 * pattern. Question-like input is routed to AI smart-search (?ai=1) instead of the literal search.
 */
export function isQuestionLike(value: string | null | undefined): boolean {
  if (!value || !value.trim()) return false;
  const lower = value.toLowerCase();
  if (lower.trim().split(/\s+/).length >= 4) return true;
  return QUESTION_PATTERNS.some((p) => lower.includes(p));
}

/** Ordered, non-empty groups of a response, optionally narrowed to one display type (a dropdown tab). */
export function orderedGroups(
  resp: SearchResponseDto | undefined,
  onlyType?: SearchHitType,
): { display: SearchHitType; labelKey: string; hits: SearchHitDto[] }[] {
  if (!resp) return [];
  return GROUP_ORDER.map((g) => ({
    display: g.display,
    labelKey: g.labelKey,
    hits: resp.groups?.[g.key] ?? [],
  })).filter((g) => g.hits.length > 0 && (onlyType == null || g.display === onlyType));
}

/** Total hits across the (optionally type-narrowed) visible groups. */
export function visibleTotal(
  resp: SearchResponseDto | undefined,
  onlyType?: SearchHitType,
): number {
  return orderedGroups(resp, onlyType).reduce((sum, g) => sum + g.hits.length, 0);
}

// ===== E4 full-results-page vocabulary (FR17–FR22) ==================================================
// Shared, pure helpers for the SearchResults page: the animal-title split, the status-badge colour
// variants, and the facet i18n-key convention. All Dutch copy lives in messages.app.ts under
// `zoekresultaten.*` (nl = the Jmix literal) — never inlined here (NFR5).

/** The server's unfiltered per-facet hit counts (Decision SS — E1 `SearchFacetCounts`). */
export interface SearchFacetCountsDto {
  animalStatus?: Record<string, number>;
  species?: Record<string, number>;
  relationType?: Record<string, number>;
  relationRole?: Record<string, number>;
  invoiceStatus?: Record<string, number>;
}

/**
 * Split an animal hit title ("naam • chipnr" — Jmix `buildAnimalTitle`) into name + chip for the card's
 * two lines (Jmix `buildAnimalCardFromHit` splits on " • "). No bullet → name only, no chip.
 */
export function splitAnimalTitle(title: string | null | undefined): { name: string; chip: string | null } {
  const full = title ?? "";
  const i = full.indexOf(" • ");
  if (i < 0) return { name: full, chip: null };
  const chip = full.slice(i + 3);
  return { name: full.slice(0, i), chip: chip.trim() ? chip : null };
}

/** Status-badge colour treatment (Jmix `srv__status-badge--<variant>`). */
export type StatusVariant = "in-care" | "available" | "neutral";

/**
 * Animal status code → badge colour variant (Jmix `buildAnimalCardFromHit` switch). UNKNOWN (no
 * residency history) renders as the "Beschikbaar" fallback so every card carries a badge.
 */
export const ANIMAL_STATUS_VARIANT: Record<string, StatusVariant> = {
  in_care: "in-care",
  foster: "in-care",
  placed: "available",
  checked_out: "neutral",
  deceased: "neutral",
  unknown: "available",
};

/** i18n key for an animal status badge label; UNKNOWN/blank → "Beschikbaar" (Jmix visual fallback). */
export function animalStatusBadgeKey(code: string | null | undefined): string {
  return !code || code === "unknown"
    ? "zoekresultaten.animalStatus.beschikbaar"
    : `zoekresultaten.animalStatus.${code}`;
}

/** i18n key for a left-panel facet item label (e.g. `zoekresultaten.species.dog`). */
export function facetLabelKey(facet: string, code: string): string {
  return `zoekresultaten.${facet}.${code}`;
}

/**
 * One result card — the animal photo (or coloured initials) + title + subtitle + type-coloured badge.
 * Shared by the header dropdown and the results route. The whole card is clickable/keyboard-activable
 * and opens the hit's detail via the shell's state nav (the consumer's `onOpen`).
 *
 * <p>The photo is a parity gap the operator noticed against acc: Jmix batch-loads each hit's first
 * photo and shows it, so you recognise the animal before you read the name. The bytes are
 * bearer-authenticated, so the caller resolves them with `useAnimalPhotoUrls` and passes the object
 * URL in. No URL (still loading, or no photo at all) falls back to the initials exactly as before.
 */
export function SearchHitCard({
  hit,
  photoUrl,
  onOpen,
}: {
  hit: SearchHitDto;
  /** Resolved `blob:` URL of this animal's photo, when the caller has one. */
  photoUrl?: string;
  onOpen: (hit: SearchHitDto) => void;
}) {
  const t = useTranslate();
  const badge = t(BADGE_KEY[hit.type]);
  const activate = () => onOpen(hit);
  return (
    <div
      className="fawkes-search-card"
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
    >
      {photoUrl ? (
        <img className="fawkes-search-card__photo" src={photoUrl} alt="" aria-hidden="true" />
      ) : (
        <span
          className={`fawkes-search-card__avatar fawkes-search-card__avatar--${hit.type}`}
          aria-hidden="true"
        >
          {hit.initials || "?"}
        </span>
      )}
      <span className="fawkes-search-card__center">
        <span className="fawkes-search-card__title">{hit.title}</span>
        {hit.subtitle ? <span className="fawkes-search-card__subtitle">{hit.subtitle}</span> : null}
      </span>
      <span className={`fawkes-search-card__badge fawkes-search-card__badge--${hit.type}`}>{badge}</span>
    </div>
  );
}
